import React from "react";
import {
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import Swal from "sweetalert2";

import QuantityCounter from "./QuantityCounter";

import {
  RemoveProductFromCart,
  UpdateProduct,
} from "../utils/productInCart";

import {
  REMOVE_ITEM,
  UPDATE_ITEM_AMOUNT,
} from "../features/productInCartSlice";
import {
  savePendingCartAmount,
  savePendingCartRemove,
} from "../helpers/pendingCartChanges";

export default function CartItemActions({
  item,
  product,
  variant = "cart",
}) {
  const dispatch = useDispatch();

  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const productName =
    product?.name ||
    item?.product?.name ||
    "המוצר";

  const removeFromCart = async () => {
    const result = await Swal.fire({
      title: "הסרת מוצר מהעגלה",
      text: `האם להסיר את ${productName} מהעגלה?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "כן, להסיר",
      cancelButtonText: "ביטול",
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        popup: "custom-swal",
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    const productId =
      item.productId ??
      item.product?.id;

    const productOptionId =
      item.productOptionId ??
      item.productOption?.id ??
      null;

    /*
      אורח:
      לא שולחים לשרת.
      שומרים שהמוצר נמחק כדי לסנכרן לאחר התחברות.
    */
    if (!loggedUser) {
      savePendingCartRemove({
        productId,
        productOptionId,
      });

      dispatch(
        REMOVE_ITEM(item.id)
      );

      await Swal.fire({
        title: "המוצר הוסר",
        text: `${productName} הוסר מהעגלה`,
        icon: "success",
        timer: 1200,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      return;
    }

    try {
      if (
        item?.id !== undefined &&
        item?.id !== null
      ) {
        await RemoveProductFromCart(
          item.id
        );
      }

      dispatch(
        REMOVE_ITEM(item.id)
      );

      await Swal.fire({
        title: "המוצר הוסר",
        text: `${productName} הוסר מהעגלה`,
        icon: "success",
        timer: 1200,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      const status =
        error.response?.status;

      /*
        הטוקן פג:
        שומרים את המחיקה מקומית כדי לסנכרן
        לאחר התחברות מחדש.
      */
      if (status === 401) {
        savePendingCartRemove({
          productId,
          productOptionId,
        });

        dispatch(
          REMOVE_ITEM(item.id)
        );

        await Swal.fire({
          title: "המוצר הוסר",
          text:
            `${productName} הוסר מהעגלה המקומית. ` +
            "השינוי יסונכרן לאחר התחברות מחדש.",
          icon: "success",
          timer: 1800,
          timerProgressBar: true,
          showConfirmButton: false,
        });

        return;
      }

      console.error(
        "שגיאה במחיקת מוצר מהעגלה:",
        error.response?.data || error
      );

      await Swal.fire({
        title: "לא ניתן להסיר את המוצר",
        text:
          error.response?.data?.message ||
          "אירעה שגיאה. נסי שוב.",
        icon: "error",
        confirmButtonText: "אישור",
      });
    }
  };

  const handleAmountChange = async (
    newAmount
  ) => {
    const numericAmount =
      Number(newAmount);

    if (
      !Number.isFinite(numericAmount) ||
      numericAmount === item.amount
    ) {
      return;
    }

    if (numericAmount <= 0) {
      await removeFromCart();
      return;
    }
    if (!loggedUser) {
      const productId =
        item.productId ??
        item.product?.id;

      const productOptionId =
        item.productOptionId ??
        item.productOption?.id ??
        null;

      dispatch(
        UPDATE_ITEM_AMOUNT({
          id: item.id,
          amount: numericAmount,
        })
      );

      savePendingCartAmount({
        productId,
        productOptionId,
        amount: numericAmount,
      });

      return;
    }

    const productInCart = {
      id: item.id,
      amount: numericAmount,
      customerId: loggedUser.id,
      productId:
        item.productId ??
        item.product?.id,
      productOptionId:
        item.productOptionId ??
        item.productOption?.id ??
        null,
      product: null,
      productOption: null,
    };

    try {
      const res =
        await UpdateProduct(
          item.id,
          productInCart
        );

      if (
        res?.status === 200 ||
        res?.status === 204
      ) {
        dispatch(
          UPDATE_ITEM_AMOUNT({
            id: item.id,
            amount: numericAmount,
          })
        );
      }
    } catch (error) {
      const status =
        error.response?.status;

      if (status === 401) {
        const productId =
          item.productId ??
          item.product?.id;

        const productOptionId =
          item.productOptionId ??
          item.productOption?.id ??
          null;

        dispatch(
          UPDATE_ITEM_AMOUNT({
            id: item.id,
            amount: numericAmount,
          })
        );

        savePendingCartAmount({
          productId,
          productOptionId,
          amount: numericAmount,
        });

        console.warn(
          "הטוקן פג. הכמות נשמרה מקומית ותסונכרן לאחר התחברות."
        );

        return;
      }

      console.error(
        "שגיאה בעדכון כמות המוצר:",
        error.response?.data || error
      );

      console.error(
        "הנתונים שנשלחו לשרת:",
        productInCart
      );

      await Swal.fire({
        title: "לא ניתן לעדכן את הכמות",
        text:
          error.response?.data?.message ||
          "השרת דחה את עדכון הכמות. נסי שוב.",
        icon: "error",
        confirmButtonText: "אישור",
      });
    }
  };

  const removeButtonLabel =
    variant === "preview"
      ? `הסרת ${productName} מהעגלה`
      : `מחיקת ${productName} מסל הקניות`;

  return (
    <Box
      component="section"
      aria-label={`פעולות עבור ${productName}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
      }}
    >
      <QuantityCounter
        value={item.amount}
        max={product?.quantity}
        onChange={handleAmountChange}
        productName={productName}
      />

      <Tooltip
        title={
          variant === "preview"
            ? "הסרה מהעגלה"
            : "מחיקה מהעגלה"
        }
        arrow
      >
        <IconButton
          type="button"
          size={
            variant === "preview"
              ? "small"
              : "medium"
          }
          onClick={removeFromCart}
          aria-label={removeButtonLabel}
          sx={{
            minWidth: 44,
            minHeight: 44,
            p:
              variant === "preview"
                ? 0.5
                : 1,
            color: "text.primary",

            "&:hover": {
              backgroundColor:
                "transparent",
              color: "inherit",
            },

            "&:focus-visible": {
              outline: "3px solid",
              outlineColor:
                "primary.main",
              outlineOffset: "3px",
            },
          }}
        >
          {variant === "preview" ? (
            <CloseIcon
              fontSize="small"
              aria-hidden="true"
            />
          ) : (
            <DeleteIcon
              aria-hidden="true"
            />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}